import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CalendarDays, Clock, UserCircle } from "lucide-react";

interface Props {
  authorId?: string | null;
  publishedAt?: string | null;
  readingMinutes?: number;
}

interface Author { id: string; name: string; photo_url: string | null; role: string | null; slug: string; }

const ArticleAuthorHeader = ({ authorId, publishedAt, readingMinutes }: Props) => {
  const [author, setAuthor] = useState<Author | null>(null);

  useEffect(() => {
    if (!authorId) { setAuthor(null); return; }
    (async () => {
      const { data } = await (supabase as any).from("blog_authors").select("id,name,photo_url,role,slug").eq("id", authorId).maybeSingle();
      setAuthor(data || null);
    })();
  }, [authorId]);

  const dateStr = publishedAt
    ? new Date(publishedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  if (!author && !dateStr) return null;

  return (
    <div className="flex items-center gap-3 py-4 border-y border-border my-6">
      {author?.photo_url ? (
        <img src={author.photo_url} alt={author.name} className="h-12 w-12 rounded-full object-cover border border-border" loading="lazy" />
      ) : (
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
          <UserCircle className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        {author && (
          <p className="font-body text-sm font-semibold text-foreground">
            <span itemProp="author" itemScope itemType="https://schema.org/Person">
              <span itemProp="name">{author.name}</span>
            </span>
            {author.role && <span className="text-muted-foreground font-normal"> · {author.role}</span>}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-0.5">
          {publishedAt && (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              <time dateTime={publishedAt} itemProp="datePublished">{dateStr}</time>
            </span>
          )}
          {readingMinutes && readingMinutes > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {readingMinutes} min de lecture
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleAuthorHeader;